import { useEffect, useState } from 'react'
import { getSingleInvestorUpdatesAPI, getSingleInvestorUpdatesPublicAPI } from '../core/_request'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BasicButton } from '../../widgets/components/UI/BasicButton'
import { useIntl } from 'react-intl'
import { DisplayImage } from '../../widgets/components/General/DisplayImage'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { DateTime } from 'luxon'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'
import { downloadFileSigned } from '../../investor-crm/core/_requests'
import { Error404 } from '../../errors/components/Error404'
import { useShortScale } from '../../../hooks/useShortScale'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { useAuth } from '../../auth'

export const InvestorUpdatePreview = () => {
  const { formatMessage } = useIntl()
  const { id } = useParams()
  const { getDateValue } = useDateFormat()
  const navigate = useNavigate()
  const location = useLocation()
  const [investorUpdate, setInvestorUpdate] = useState<any>()
  const [error, setError] = useState<any>()
  const [active, setActive] = useState<string>()
  const { convertValueToShortScale } = useShortScale()
  const { companyId, currentUser } = useAuth()

  const getSingleInvestorUpdateData = async () => {
    try {
      if (location.pathname.split('/')[2] === 'preview') {
        const {
          data: { data: singleInvestorUpdate, success },
        } = await getSingleInvestorUpdatesAPI(+id!)
        if (success) {
          setError(false)
          setInvestorUpdate({ ...singleInvestorUpdate })
        } else {
          setError(true)
        }
      } else {
        const {
          data: { data: singleInvestorUpdate, success },
        } = await getSingleInvestorUpdatesPublicAPI(id!)
        if (success) {
          if (singleInvestorUpdate.status === 'PUBLISHED') {
            setError(false)
          }
          setInvestorUpdate({ ...singleInvestorUpdate })
        } else {
          setError(true)
        }
      }
    } catch (err) {
      setError(true)
      console.log(err)
    }
  }
  useEffect(() => {
    if (id) {
      getSingleInvestorUpdateData()
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const getTextFromHTML = (html: any) => {
    return <div dangerouslySetInnerHTML={{ __html: html }} />
  }

  const formatFileName = (input: string) => {
    return input.replace(/-\d{13}\.\w+/g, '')
  }

  const onDownloadFile = async (activityName: string) => {
    const {
      data: { data: signedUrl, success },
    } = await downloadFileSigned(activityName)
    if (success) {
      const a = document.createElement('a')
      a.href = signedUrl
      a.download = 'download'
      document.body.appendChild(a)
      a.click()
      a.remove()
      document.body.removeChild(a)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DownloadSignedFileSuccessful,
          action: 'normal',
          metadata: {
            activity_name: activityName,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
          },
        },
      })
    }
  }
  const handleClick = (id: string) => {
    const section = document.getElementById(id)
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    setActive(id)
  }

  useEffect(() => {
    if (investorUpdate) {
      setActive(JSON.parse(investorUpdate?.content)[0].type + 0 + 's')
    }
  }, [investorUpdate])

  return (
    <>
      {error === false && (
        <div className='mx-10 my-5'>
          <div className='update_preview_block'>
            {location.pathname.split('/')[2] === 'preview' && (
              <div className='d-md-flex justify-content-between py-5 px-3 gap-3'>
                <p className='w-md-50 m-0 mb-3 mb-md-0'>
                  {formatMessage({
                    id: 'This is how investors will see the update. You can click publish button to save the update and generate a unique update URL, which you can send to your investors.',
                  })}
                </p>
                <BasicButton
                  buttonText={formatMessage({ id: 'Back to the app' })}
                  height='36px'
                  border='none'
                  color='#4776E6'
                  textColor='#FFFFFF'
                  padding='8px 24px'
                  width='175px'
                  onClick={() => {
                    navigate('/')
                  }}
                />
              </div>
            )}

            <div className='update_preview_info py-md-8 px-md-12 py-4 px-6'>
              <h1 className='m-0 mb-8'>{investorUpdate?.title}</h1>
              <div className='d-flex justify-content-between align-items-center gap-2'>
                <div className='d-flex gap-8'>
                  {investorUpdate?.showLogo && (
                    <div className='rounded'>
                      <DisplayImage
                        imgName={investorUpdate?.company?.file?.name}
                        width='65px'
                        height='65px'
                        alt='profile'
                        fit='fill'
                        className='round_image_design'
                      />
                    </div>
                  )}

                  <div className='d-flex flex-column justify-content-center'>
                    {investorUpdate?.showAuthor && (
                      <>
                        <p className='m-0 '>
                          {formatMessage({
                            id: 'Published By',
                          })}
                        </p>
                        <p className='m-0 mb-3'>{`${investorUpdate?.user?.firstName} ${investorUpdate?.user?.lastName}`}</p>
                      </>
                    )}

                    <div className='internal_type m-0 d-flex justify-content-center'>
                      {investorUpdate?.type.charAt(0).toUpperCase() +
                        investorUpdate?.type.slice(1).toLowerCase() +
                        ' ' +
                        'Update'}
                    </div>
                  </div>
                </div>
                <div>
                  <p className='m-0 mb-3'>{formatMessage({ id: 'Update timeframe' })}</p>
                  {investorUpdate && investorUpdate.timeframe ? (
                    <p className='m-0'>
                      {getDateValue(JSON.parse(investorUpdate.timeframe)?.[0]?.replace(' ', 'T')) ||
                        DateTime.fromISO(
                          JSON.parse(investorUpdate.timeframe)?.[0]?.replace(' ', 'T')
                        ).toLocaleString(DateTime.DATE_HUGE)}
                      {' - '}
                      {getDateValue(JSON.parse(investorUpdate.timeframe)?.[1]?.replace(' ', 'T')) ||
                        DateTime.fromISO(
                          JSON.parse(investorUpdate.timeframe)?.[1]?.replace(' ', 'T')
                        ).toLocaleString(DateTime.DATE_HUGE)}
                    </p>
                  ) : (
                    ''
                  )}
                </div>
              </div>
            </div>
            <div className='d-flex section justify-content-center p-md-10 p-5 gap-8'>
              <div className='d-flex flex-column gap-3 mw-200px w-100'>
                {investorUpdate?.content &&
                  JSON.parse(investorUpdate?.content).map((section: any, index: number) => {
                    switch (section.type) {
                      case 'richText':
                        return (
                          <div
                            className={`section-title cursor-pointer ${active === section.type + index + 's' && 'section-title-active'
                              }`}
                            key={section.type + index + 't'}
                            onClick={() => handleClick(section.type + index + 's')}
                          >
                            <p className='m-0'>
                              {section.inputText ? section.inputText : section.type}
                            </p>
                          </div>
                        )
                      case 'kpiValues':
                        return (
                          <div
                            className={`section-title cursor-pointer ${active === section.type + index + 's' && 'section-title-active'
                              }`}
                            key={section.type + index + 't'}
                            onClick={() => handleClick(section.type + index + 's')}
                          >
                            <p className='m-0'>
                              {formatMessage({
                                id: 'KPI',
                              })}
                            </p>
                          </div>
                        )
                      case 'documents':
                        return (
                          <div
                            className={`section-title cursor-pointer ${active === section.type + index + 's' && 'section-title-active'
                              }`}
                            key={section.type + index + 't'}
                            onClick={() => handleClick(section.type + index + 's')}
                          >
                            <p className='m-0'>
                              {section.inputText ? section.inputText : section.type}
                            </p>
                          </div>
                        )

                      default:
                        return null
                    }
                  })}
              </div>
              <div className='d-flex flex-column gap-8 flex-grow-1'>
                {investorUpdate?.content &&
                  JSON.parse(investorUpdate?.content).map((section: any, index: number) => {
                    switch (section.type) {
                      case 'richText':
                        return (
                          <div
                            key={section.type + index + 's'}
                            id={section.type + index + 's'}
                            className='d-flex flex-column gap-3'
                          >
                            <h2 className='m-0 font-size-16 font-weight-600'>
                              {section.inputText ? section.inputText : section.type}
                            </h2>
                            <p className='m-0 font-size-13 text-muted'>
                              {getTextFromHTML(section?.richText)}
                            </p>
                          </div>
                        )
                      case 'kpiValues':
                        return (
                          <div
                            key={section.type + index + 's'}
                            className='d-flex flex-column gap-3'
                            id={section.type + index + 's'}
                          >
                            <h2 className='m-0 font-size-16 font-weight-600'>
                              {formatMessage({
                                id: 'KPI',
                              })}
                            </h2>
                            <div className='d-flex gap-5 flex-wrap'>
                              {section.kpiValues.map((kpi: any) => {
                                return (
                                  <>
                                    {kpi?.toogle && (
                                      <div
                                        key={kpi.id}
                                        className=' d-flex flex-column justify-content-start kpi_preview_box pt-3 ps-8 w-200px h-125px'
                                      >
                                        <div className=' font-size-32 font-weight-500'>
                                          {kpi.type === 'Currency' ? (
                                            <p className='mb-0'>
                                              <span className='font-size-16'>
                                                {section?.currencyType}
                                              </span>
                                              {convertValueToShortScale(kpi.value)}
                                            </p>
                                          ) : kpi.type === 'Percentage' ? (
                                            <p className='mb-0'>
                                              {kpi.value}
                                              <span className='font-size-20'>{`%`}</span>
                                            </p>
                                          ) : kpi.value ? (
                                            <p className='mb-0'>{kpi.value}</p>
                                          ) : (
                                            <p className='mb-0'>{`-`}</p>
                                          )}
                                        </div>
                                        <p className='m-0 font-size-16 font-weight-500'>
                                          {kpi.title}
                                        </p>
                                      </div>
                                    )}
                                  </>
                                )
                              })}
                            </div>
                          </div>
                        )
                      case 'documents':
                        return (
                          <div
                            key={section.type + index + 's'}
                            className='d-flex flex-column gap-3'
                            id={section.type + index + 's'}
                          >
                            <h2 className='m-0 font-size-16 font-weight-600'>
                              {section.inputText ? section.inputText : section.type}
                            </h2>
                            <div className='d-flex flex-wrap gap-5'>
                              {section?.ids?.map((file: any) => {
                                return (
                                  <div
                                    className='d-flex w-300px justify-content-between align-items-center h-30px'
                                    key={file.id}
                                  >
                                    {file.name?.split('.').pop() === 'pdf' ? (
                                      <>
                                        <div className='d-flex gap-3'>
                                          <img
                                            alt=''
                                            className='w-20px'
                                            src={toAbsoluteUrl(`/media/icons/file_extensions/${file.name?.split('.').pop()}.svg`)}
                                          />

                                          <p className='fs-6 m-0'>{formatFileName(file.name)}</p>
                                        </div>
                                        <img
                                          alt='download'
                                          className='w-20px me-3 cursor-pointer'
                                          onClick={() => onDownloadFile(file.name)}
                                          src={toAbsoluteUrl('/media/svg/files/download.svg')}
                                        />
                                      </>
                                    ) : file.name?.split('.').pop() === 'docx' ? (
                                      <>
                                        <div className='d-flex gap-3'>
                                          <img
                                            alt=''
                                            className='w-20px'
                                            src={toAbsoluteUrl(`/media/icons/file_extensions/${file.name?.split('.').pop()}.svg`)}
                                          />

                                          <p className='fs-6 m-0'>{formatFileName(file.name)}</p>
                                        </div>
                                        <img
                                          alt='download'
                                          className='w-20px me-3 cursor-pointer'
                                          onClick={() => onDownloadFile(file.name)}
                                          src={toAbsoluteUrl('/media/svg/files/download.svg')}
                                        />
                                      </>
                                    ) : file.name?.split('.').pop() === 'png' ||
                                      file.name?.split('.').pop() === 'jpg' ? (
                                      <>
                                        <div className='d-flex gap-3'>
                                          <img
                                            alt=''
                                            className='w-20px'
                                            src={toAbsoluteUrl('/media/svg/files/blank-image.svg')}
                                          />

                                          <p className='fs-6 m-0'>{formatFileName(file.name)}</p>
                                        </div>
                                        <img
                                          alt='download'
                                          className='w-20px me-3 cursor-pointer'
                                          onClick={() => onDownloadFile(file.name)}
                                          src={toAbsoluteUrl('/media/svg/files/download.svg')}
                                        />
                                      </>
                                    ) : (
                                      <div className='d-flex w-300px justify-content-between align-items-center h-30px'>
                                        <p className='fs-6'>{file.name}</p>
                                        <img
                                          alt='download'
                                          className='w-20px me-3 cursor-pointer'
                                          onClick={() => onDownloadFile(file.name)}
                                          src={toAbsoluteUrl('/media/svg/files/download.svg')}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        )
                      case 'lineBreak':
                        return (
                          <div
                            key={section.type + index + 's'}
                            className='border border-bottom-2'
                          ></div>
                        )
                      default:
                        return null
                    }
                  })}
              </div>
            </div>
          </div>
        </div>
      )}

      {error === true && (
        <div className='d-flex justify-content-center w-100'>
          <div className='center_page text-center mw-900px px-md-20 px-3'>
            <Error404 />
          </div>
        </div>
      )}
    </>
  )
}
